import { aktionsfarbenAus } from "../farben.js";
/**
 * Die Unternehmensfarbe des Heimatunternehmens am Dokument (seit ME-Etappe 1, Aufgabe 4).
 *
 * **Bekannte Unternehmen** (me|bs|pa) färbt me-design selbst: `data-unternehmen` am
 * `<html>` schaltet dort --marke/--aktion, hell wie dunkel; der Akzent-Alias ist in kundeninfo.css
 * nur noch ein Alias auf --aktion. Hier wird dann nur das Attribut gesetzt.
 *
 * **Alle übrigen** (weitere Unternehmen sind grundsätzlich anlegbar, Dirks Entscheid)
 * bekommen einen Rückfall-Stilblock, der --marke und --aktion aus `aktionsfarbenAus`
 * setzt — dieselbe Rechnung, aus der me-design seine drei Werte hat. Die Schrift auf dem
 * Aktionston setzt der Block NICHT: der Alias für die Aktionsschrift ist ein Alias auf --aktion-schrift, und
 * das ist in me-design hell Weiß, dunkel #0b0b0b — auf dem AUFGEHELLTEN Dunkelton trägt
 * diese dunkle Schrift (geprüft in test/aktionsfarben.test.ts).
 *
 * **Warum ein eingehängter Stilblock und nicht `documentElement.style.setProperty`:**
 * Eine Inline-Eigenschaft am `<html>` schlägt JEDE Regel der ME-Tokendatei, auch die
 * beiden Dunkel-Blöcke. Der Dunkelmodus bekäme dann den abgedunkelten HELLTON — genau die
 * Farbe, die auf dunklem Grund nicht mehr trägt, und der Umschalter „Hell/Dunkel" hätte
 * auf den Akzent keine Wirkung mehr. Der Block hier hat deshalb DIESELBE Kaskadenform wie
 * die ME-Datei (Hell, Dunkel-vom-Gerät, Dunkel-gewählt) und wird nur später eingehängt:
 * Bei gleicher Spezifität gewinnt der spätere, und zwar in jedem der drei Fälle der
 * richtige.
 */
/** Die Kennung des eingehängten Stilblocks — eine Stelle für Setzen, Ersetzen und Fortnehmen. */
export const AKTIONSFARBEN_STIL_ID = "ki-aktionsfarben";
/** Die Unternehmen, deren Farben me-design selbst kennt (`data-unternehmen`, klein). */
export const ME_UNTERNEHMEN = new Set(["me", "bs", "pa"]);
/**
 * Die drei Kaskadenblöcke als CSS-Text — rein, ohne DOM. Die Werte kommen aus
 * `aktionsfarbenAus` und sind dort buchstäblich als `#RRGGBB` gebaut; `marke` ist die
 * Eingabe dieser Ableitung, die dort bereits als `#rrggbb` geprüft wurde. Es kann also
 * nichts anderes in den Stilblock geraten als eine Farbe.
 */
export function aktionsfarbenCss(marke, farben) {
    const dunkel = `--aktion: ${farben.hellFuerDunkelmodus};`;
    return [
        "/* Unternehmensfarbe (Rückfall für Unternehmen, die me-design nicht kennt). */",
        `:root { --marke: ${marke}; --aktion: ${farben.aktion}; }`,
        "@media (prefers-color-scheme: dark) {",
        `  :root:not([data-theme="hell"]) { ${dunkel} }`,
        "}",
        `:root[data-theme="dunkel"] { ${dunkel} }`,
        "",
    ].join("\n");
}
/**
 * Setzt die Farbe des ARBEITSUNTERNEHMENS am Dokument. `colorHex === null` (kein
 * Arbeitsunternehmen bestimmbar) nimmt Attribut und Stilblock ganz fort — dann gilt die
 * ME-Vorgabe aus den me-design-Tokens.
 *
 * `kuerzel` ist das Unternehmenskürzel (company.shortCode); me-design kennt me/bs/pa und
 * färbt sie über `data-unternehmen` selbst. Ein Aufruf mit Farbe, aber OHNE Kürzel
 * (Aufrufer außerhalb der App, Tests) setzt den Rückfall-Stilblock wie für ein
 * unbekanntes Kürzel.
 *
 * Eine unbrauchbare Farbe meldet sich in der Konsole und lässt die Vorgabe stehen,
 * statt zu werfen: `aktionsfarbenAus` ist zurecht laut (ein stiller Rückfall auf Schwarz
 * ergäbe eine Oberfläche, die falsch aussieht, ohne dass jemand erführe warum), aber
 * dieser Aufruf hängt am Sitzungsaufbau. Ein Wurf dort ersetzte die ganze Anwendung durch
 * eine Fehlerseite — wegen einer Farbe. Die Meldung geht deshalb an die Konsole, die
 * Arbeit geht weiter.
 */
export function setzeAktionsfarben(colorHex, kuerzel = null) {
    // Erst fortnehmen, dann neu setzen: Scheitert die Ableitung, bleibt sonst die Farbe des
    // VORIGEN Benutzers stehen — falscher als die Vorgabe.
    const wurzel = document.documentElement;
    document.getElementById(AKTIONSFARBEN_STIL_ID)?.remove();
    if (colorHex === null) {
        delete wurzel.dataset.unternehmen;
        return;
    }
    const unternehmen = kuerzel?.toLowerCase() ?? null;
    if (unternehmen === null)
        delete wurzel.dataset.unternehmen;
    else
        wurzel.dataset.unternehmen = unternehmen;
    if (unternehmen !== null && ME_UNTERNEHMEN.has(unternehmen))
        return;
    let css;
    try {
        css = aktionsfarbenCss(colorHex, aktionsfarbenAus(colorHex));
    }
    catch (ursprung) {
        console.error("Die Aktionsfarbe des Heimatunternehmens konnte nicht abgeleitet werden; es gelten "
            + "die Vorgabefarben.", ursprung);
        return;
    }
    const stil = document.createElement("style");
    stil.id = AKTIONSFARBEN_STIL_ID;
    stil.textContent = css;
    document.head.appendChild(stil);
}
//# sourceMappingURL=aktionsfarben.js.map